import {
  addDays,
  awardForDreamCompleted,
  awardForFocus,
  awardForGoalCompleted,
  awardForGoalMilestone,
  awardsForHabit,
  awardForJournal,
  awardForMood,
  awardsForIntention,
  awardsForReview,
  awardsForWheel,
  computeProgression,
  GENERAL_DIMENSION,
  habitStreak,
  isDueOn,
  periodKeyFor,
  seasonKeyFor,
  type PlanAward,
  type ProgressionEvent,
  type ProgressionState,
  type TaskAward,
} from '@selflevel/domain';
import {
  db,
  type DayPlan,
  type Dream,
  type FocusSession,
  type Goal,
  type GoalMilestone,
  type Habit,
  type HabitEntry,
  type Task,
} from './db';

/**
 * DERIVAR, NO GUARDAR.
 *
 * Nada de lo que se calcula aquí se almacena: ni los puntos, ni el nivel, ni las
 * rachas, ni los hitos. Todo se recalcula desde el historial en cada lectura.
 *
 * Parece caro y no lo es: un año de un usuario activo son unas 4.000 filas, y
 * recorrerlas es cuestión de milisegundos. A cambio desaparece la clase entera
 * de bugs de «mi racha dice 12 en el móvil y 9 en el portátil», porque no hay
 * dos copias de un contador que puedan discrepar — no hay contador.
 */

export type ProgressionInput = {
  today: string;
  levelFloor: number;
  sealedMilestones: readonly string[];
};

/** Todos los eventos de progresión del historial local. */
export async function collectProgressionEvents(): Promise<ProgressionEvent[]> {
  const [
    habits,
    entries,
    sessions,
    journals,
    moods,
    reviews,
    areas,
    planes,
    tareas,
    ruedas,
    metas,
    hitos,
    suenos,
  ] = await Promise.all([
    db.habits.toArray(),
    db.habitEntries.toArray(),
    db.focusSessions.toArray(),
    db.journalEntries.toArray(),
    db.moodLogs.toArray(),
    db.reviews.toArray(),
    db.lifeAreas.toArray(),
    db.dayPlans.toArray(),
    db.tasks.toArray(),
    db.lifeWheels.toArray(),
    db.goals.toArray(),
    db.goalMilestones.toArray(),
    db.dreams.toArray(),
  ]);

  const areaOfHabit = new Map(habits.map((h) => [h.id, h.areaId]));
  // Un mapa y no un `habits.find()` por registro. El `find` convertía la vuelta
  // en O(registros × hábitos): cinco años de diez hábitos son 18.000 registros
  // y 180.000 comparaciones en cada recálculo —y esto se recalcula cada vez que
  // se marca una casilla—. El mapa de al lado ya existía; faltaba este.
  const habitById = new Map(habits.map((h) => [h.id, h]));
  const events: ProgressionEvent[] = [];

  for (const entry of entries) {
    if (entry.deletedAt) continue;
    const habit = habitById.get(entry.habitId);
    const dimension = areaOfHabit.get(entry.habitId);
    if (!dimension) continue;
    // El hábito que trabaja varias áreas (v4): la misma función que el servidor.
    events.push(
      ...awardsForHabit(
        {
          entryId: entry.id,
          localDate: entry.localDate,
          dimension,
          status: entry.status,
          value: entry.value ?? undefined,
          target: habit?.target ?? undefined,
          attested: entry.attested,
        },
        habit?.extraAreaIds ?? [],
      ),
    );
  }

  for (const session of sesionesDeFocoVigentes(sessions)) {
    const award = awardForFocus({
      sessionId: session.id,
      localDate: session.localDate,
      dimension: dimensionDeFoco(session, areaOfHabit),
      actualMinutes: session.actualMinutes,
      interrupted: session.interrupted,
      attested: session.attested,
    });
    if (award) events.push(award);
  }

  for (const journal of journals) {
    if (journal.deletedAt) continue;
    events.push(awardForJournal(journal.id, journal.localDate, true));
  }

  for (const mood of moods) {
    events.push(awardForMood(mood.id, mood.localDate, mood.attested));
  }

  const activeAreas = areas.filter((a) => !a.archivedAt).map((a) => a.id);
  for (const review of reviews) {
    if (!review.completedAt) continue;
    events.push(
      ...awardsForReview(
        review.id,
        review.localDate,
        review.periodType,
        activeAreas,
        review.attested,
      ),
    );
  }

  // La rueda de la vida sellada (v3): una por mes, repartida como la revisión.
  for (const rueda of ruedas) {
    if (rueda.deletedAt || !rueda.sealedAt || !rueda.localDate) continue;
    events.push(...awardsForWheel(rueda.monthKey, rueda.localDate, activeAreas, rueda.attested));
  }

  const intencion = filasDeIntencion(planes, tareas);
  events.push(...awardsForIntention(intencion.plans, intencion.tasks));
  events.push(...eventosDeMetasYSuenos(metas, hitos, suenos));
  return events;
}

/**
 * METAS Y SUEÑOS (v5), con la regla del servidor (`packages/db/src/progresion.ts`):
 * solo lo cumplido que lleva su día lógico, y los sub-hitos de metas que existen y no
 * están borradas, en el área de la meta. Los pasos de un sueño no puntúan.
 */
export function eventosDeMetasYSuenos(
  metas: readonly Pick<Goal, 'id' | 'areaId' | 'status' | 'localDate' | 'deletedAt'>[],
  hitos: readonly Pick<GoalMilestone, 'id' | 'goalId' | 'doneAt' | 'localDate' | 'deletedAt'>[],
  suenos: readonly (Pick<Dream, 'id' | 'areaId' | 'status' | 'localDate'> & {
    deletedAt?: string | null;
  })[],
): ProgressionEvent[] {
  const eventos: ProgressionEvent[] = [];
  const areaDeMeta = new Map(metas.filter((m) => !m.deletedAt).map((m) => [m.id, m.areaId]));
  for (const meta of metas) {
    if (meta.deletedAt || meta.status !== 'cumplida' || !meta.localDate) continue;
    eventos.push(awardForGoalCompleted(meta.id, meta.localDate, meta.areaId, true));
  }
  for (const hito of hitos) {
    const area = areaDeMeta.get(hito.goalId);
    if (hito.deletedAt || !hito.doneAt || !hito.localDate || !area) continue;
    eventos.push(awardForGoalMilestone(hito.id, hito.localDate, area, true));
  }
  for (const sueno of suenos) {
    if (sueno.deletedAt || sueno.status !== 'cumplida' || !sueno.localDate) continue;
    eventos.push(awardForDreamCompleted(sueno.id, sueno.localDate, sueno.areaId, true));
  }
  return eventos;
}

/**
 * Las filas del plan del día y de las tareas, en la forma que puntúa el dominio.
 * Las mismas columnas que lee el servidor (`packages/db/src/progresion.ts`): si una
 * difiere, el nivel del móvil y el sellado discrepan (P8).
 */
export function filasDeIntencion(
  planes: readonly DayPlan[],
  tareas: readonly Task[],
): { plans: PlanAward[]; tasks: TaskAward[] } {
  return {
    plans: planes
      .filter((p) => !p.deletedAt)
      .map((p) => ({
        localDate: p.localDate,
        pillarAreaId: p.pillarAreaId,
        frogTaskId: p.frogTaskId,
        ritualSealed: p.ritualSealedAt !== null,
        attested: p.attested,
      })),
    tasks: tareas
      .filter((t) => !t.deletedAt)
      .map((t) => ({
        taskId: t.id,
        plannedDate: t.plannedDate,
        areaId: t.areaId,
        doneLocalDate: t.doneLocalDate,
        attested: t.attested,
      })),
  };
}

/**
 * La dimensión de una sesión de foco: LA MISMA REGLA que el servidor
 * (`packages/db/src/progresion.ts`), o el nivel del móvil y el sellado
 * discreparían (P8). Manda el área del hábito; si no hay hábito, la de la
 * sesión; y el foco sin destino va a `general`, no se descarta.
 */
export function dimensionDeFoco(
  session: Pick<FocusSession, 'habitId' | 'areaId'>,
  areaOfHabit: ReadonlyMap<string, string>,
): string {
  const habitArea = session.habitId ? areaOfHabit.get(session.habitId) : undefined;
  return habitArea ?? session.areaId ?? GENERAL_DIMENSION;
}

/** Las sesiones que cuentan: la corregida por otra (`supersedesId`) ya no. */
export function sesionesDeFocoVigentes<T extends Pick<FocusSession, 'id' | 'supersedesId'>>(
  sessions: readonly T[],
): T[] {
  const sustituidas = new Set<string>();
  for (const s of sessions) if (s.supersedesId) sustituidas.add(s.supersedesId);
  return sessions.filter((s) => !sustituidas.has(s.id));
}

export async function computeLocalProgression(input: ProgressionInput): Promise<ProgressionState> {
  const events = await collectProgressionEvents();
  return computeProgression(events, {
    today: input.today,
    levelFloor: input.levelFloor,
    sealedMilestones: input.sealedMilestones,
  });
}

// ===========================================================================
// LA VISTA DE HOY
// ===========================================================================

export type TodayHabit = {
  habit: Habit;
  entry: HabitEntry | undefined;
  /** Hechos dentro del periodo de la cuota (semana o mes). */
  doneInPeriod: number;
  target: number;
  streak: number;
};

/**
 * Lo que toca hoy.
 *
 * **Un hábito que no toca hoy no aparece.** No sale en gris, no sale tachado, no
 * sale «opcional»: la lista de Hoy es la lista de lo que toca, y por eso se
 * puede terminar. Una lista que nunca se vacía no es una lista, es un reproche.
 */
export async function loadToday(localDate: string): Promise<TodayHabit[]> {
  const habits = (await db.habits.toArray())
    .filter((h) => !h.archivedAt)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  // UNA consulta para todos los hábitos, no una por hábito. Antes era un
  // `await` dentro del bucle: diez hábitos, diez viajes a IndexedDB uno detrás
  // de otro, y Hoy —la pantalla que más se abre y donde más se marca— los
  // repetía enteros cada vez que cambiaba una casilla.
  const porHabito = new Map<string, HabitEntry[]>();
  if (habits.length > 0) {
    const todas = await db.habitEntries
      .where('habitId')
      .anyOf(habits.map((h) => h.id))
      .toArray();
    for (const entry of todas) {
      const lista = porHabito.get(entry.habitId);
      if (lista) lista.push(entry);
      else porHabito.set(entry.habitId, [entry]);
    }
  }

  const out: TodayHabit[] = [];

  for (const habit of habits) {
    const entries = porHabito.get(habit.id) ?? [];
    const alive = entries.filter((e) => !e.deletedAt);

    const periodKey = periodKeyFor(habit.cadence, localDate);
    const doneInPeriod = alive.filter(
      (e) => e.status === 'done' && periodKeyFor(habit.cadence, e.localDate) === periodKey,
    ).length;

    const entry = alive.find((e) => e.localDate === localDate);
    const due = isDueOn(habit.cadence, localDate, doneInPeriod);

    // Si ya se registró hoy, se enseña aunque la cuota esté hecha: lo que ya
    // hiciste no desaparece de la vista del día en que lo hiciste.
    if (!due && !entry) continue;

    out.push({
      habit,
      entry,
      doneInPeriod,
      target:
        habit.cadence.kind === 'daily' || habit.cadence.kind === 'weekdays'
          ? 1
          : habit.cadence.times,
      streak: habitStreak(
        alive.map((e) => ({ localDate: e.localDate, status: e.status, tz: e.tz })),
        habit.cadence,
        localDate,
      ),
    });
  }

  return out;
}

// ===========================================================================
// MAPA DE CALOR Y ESTADÍSTICA
// ===========================================================================

export type HeatDay = { localDate: string; points: number; entries: number };

/**
 * Cuántos registros hechos hubo cada día del tramo. Es la única parte del mapa
 * de calor que hay que CONSULTAR: los puntos de cada día ya los tiene la
 * progresión compartida en `byDay`, y recalcular la historia entera para
 * leerlos era un tercer recorrido completo por cada casilla marcada.
 */
export async function loadHeatCounts(from: string, to: string): Promise<Map<string, number>> {
  const entries = await db.habitEntries.where('localDate').between(from, to, true, true).toArray();
  const countByDay = new Map<string, number>();
  for (const entry of entries) {
    if (entry.deletedAt || entry.status !== 'done') continue;
    countByDay.set(entry.localDate, (countByDay.get(entry.localDate) ?? 0) + 1);
  }
  return countByDay;
}

/** El mapa de calor ya montado: una casilla por día del tramo. Puro. */
export function buildHeatmap(
  from: string,
  to: string,
  byDay: ReadonlyMap<string, number>,
  countByDay: ReadonlyMap<string, number>,
): HeatDay[] {
  const out: HeatDay[] = [];
  let cursor = from;
  while (cursor <= to) {
    out.push({
      localDate: cursor,
      points: byDay.get(cursor) ?? 0,
      entries: countByDay.get(cursor) ?? 0,
    });
    cursor = addDays(cursor, 1);
    if (out.length > 800) break;
  }
  return out;
}

/** Las temporadas que ya deberían estar selladas y todavía no lo están. */
export async function pendingSeasonsToSeal(today: string): Promise<string[]> {
  const events = await collectProgressionEvents();
  const current = seasonKeyFor(today);

  const seen = new Set<string>();
  for (const event of events) seen.add(seasonKeyFor(event.localDate));

  const sealed = new Set((await db.seasons.toArray()).map((s) => s.key));
  return [...seen].filter((key) => key !== current && !sealed.has(key)).sort();
}
